/**
 * Minimal, dependency-free .xlsx writer for the browser.
 *
 * An .xlsx file is a ZIP of XML parts. ZIP allows STORE (no compression), and
 * OOXML allows inline strings (no shared-strings table), so a valid workbook
 * can be assembled with nothing but a CRC32 and some string building — no
 * SheetJS, no zip library, no new npm dependency.
 *
 * That matters here: this repo cannot currently `npm install` cleanly (an
 * Angular 11-era peer dependency conflicts with Angular 18), so adding a
 * package to generate a spreadsheet would drag in a dependency-resolution
 * problem unrelated to the feature.
 *
 * Scope is deliberately small — text/number cells, column widths, a frozen
 * header row and an autofilter. No formulas, styles, merges or dates-as-serial
 * (timestamps are written as text, which is unambiguous in every locale).
 *
 * Usage:
 *   const blob = buildXlsx([{ name: 'Failed Records', rows, colWidths }]);
 *   downloadBlob(blob, 'failures.xlsx');
 */

export type CellValue = string | number | null | undefined;

export interface XlsxSheet {
    /** Sheet tab name. Invalid characters are stripped, length capped at 31. */
    name: string;
    /** Row 0 is treated as the header row (frozen + autofiltered). */
    rows: CellValue[][];
    /** Optional per-column widths, in approximate character units. */
    colWidths?: number[];
}

// ─────────────────────────── ZIP (store-only) ───────────────────────────

const CRC_TABLE: number[] = (() => {
    const table = new Array<number>(256);
    for (let i = 0; i < 256; i++) {
        let c = i;
        for (let k = 0; k < 8; k++) {
            c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        }
        table[i] = c >>> 0;
    }
    return table;
})();

function crc32(bytes: Uint8Array): number {
    let c = 0xffffffff;
    for (let i = 0; i < bytes.length; i++) {
        c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
    }
    return (c ^ 0xffffffff) >>> 0;
}

interface ZipEntry {
    name: string;
    data: Uint8Array;
}

/** MS-DOS packed date/time, as ZIP headers require. */
function dosDateTime(d: Date): { time: number; date: number } {
    const time = ((d.getHours() & 0x1f) << 11) | ((d.getMinutes() & 0x3f) << 5) | ((d.getSeconds() / 2) & 0x1f);
    const date = (((d.getFullYear() - 1980) & 0x7f) << 9) | (((d.getMonth() + 1) & 0x0f) << 5) | (d.getDate() & 0x1f);
    return { time, date };
}

/**
 * Build a ZIP with every entry stored uncompressed. Sizes and CRCs are known
 * up front, so no data descriptors are needed.
 */
function zipStore(entries: ZipEntry[]): Blob {
    const encoder = new TextEncoder();
    const { time, date } = dosDateTime(new Date());
    const locals: Uint8Array[] = [];
    const centrals: Uint8Array[] = [];
    let offset = 0;

    for (const entry of entries) {
        const nameBytes = encoder.encode(entry.name);
        const crc = crc32(entry.data);
        const size = entry.data.length;

        const local = new Uint8Array(30 + nameBytes.length);
        const lv = new DataView(local.buffer);
        lv.setUint32(0, 0x04034b50, true);  // local file header signature
        lv.setUint16(4, 20, true);          // version needed
        lv.setUint16(6, 0x0800, true);      // flags: UTF-8 names
        lv.setUint16(8, 0, true);           // method: store
        lv.setUint16(10, time, true);
        lv.setUint16(12, date, true);
        lv.setUint32(14, crc, true);
        lv.setUint32(18, size, true);       // compressed size
        lv.setUint32(22, size, true);       // uncompressed size
        lv.setUint16(26, nameBytes.length, true);
        lv.setUint16(28, 0, true);          // extra field length
        local.set(nameBytes, 30);

        const central = new Uint8Array(46 + nameBytes.length);
        const cv = new DataView(central.buffer);
        cv.setUint32(0, 0x02014b50, true);  // central directory signature
        cv.setUint16(4, 20, true);          // version made by
        cv.setUint16(6, 20, true);          // version needed
        cv.setUint16(8, 0x0800, true);
        cv.setUint16(10, 0, true);
        cv.setUint16(12, time, true);
        cv.setUint16(14, date, true);
        cv.setUint32(16, crc, true);
        cv.setUint32(20, size, true);
        cv.setUint32(24, size, true);
        cv.setUint16(28, nameBytes.length, true);
        cv.setUint16(30, 0, true);          // extra
        cv.setUint16(32, 0, true);          // comment
        cv.setUint16(34, 0, true);          // disk number
        cv.setUint16(36, 0, true);          // internal attrs
        cv.setUint32(38, 0, true);          // external attrs
        cv.setUint32(42, offset, true);     // offset of local header
        central.set(nameBytes, 46);

        locals.push(local, entry.data);
        centrals.push(central);
        offset += local.length + size;
    }

    const centralSize = centrals.reduce((sum, c) => sum + c.length, 0);
    const end = new Uint8Array(22);
    const ev = new DataView(end.buffer);
    ev.setUint32(0, 0x06054b50, true);      // end of central directory
    ev.setUint16(8, entries.length, true);
    ev.setUint16(10, entries.length, true);
    ev.setUint32(12, centralSize, true);
    ev.setUint32(16, offset, true);

    return new Blob([...locals, ...centrals, end], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
}

// ─────────────────────────────── OOXML ───────────────────────────────

/**
 * Escape for XML text, and drop the control characters XML 1.0 forbids.
 * Error messages in this report can carry raw stack traces, so this is not
 * optional: an unescaped `&` or a stray 0x00-0x1F byte makes the workbook
 * unopenable in Excel.
 */
function xmlEscape(value: string): string {
    return value
        // eslint-disable-next-line no-control-regex
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

/** A1-style column name: 0 -> A, 25 -> Z, 26 -> AA. */
function columnName(index: number): string {
    let name = '';
    let n = index;
    while (n >= 0) {
        name = String.fromCharCode(65 + (n % 26)) + name;
        n = Math.floor(n / 26) - 1;
    }
    return name;
}

/** Excel rejects : \ / ? * [ ] in tab names, and caps them at 31 characters. */
function safeSheetName(name: string, fallbackIndex: number): string {
    const cleaned = String(name || '').replace(/[:\\/?*[\]]/g, ' ').trim();
    return (cleaned || `Sheet${fallbackIndex + 1}`).slice(0, 31);
}

function sheetXml(sheet: XlsxSheet): string {
    const rows = sheet.rows || [];
    const colCount = rows.reduce((max, r) => Math.max(max, r.length), 0);
    const lastCol = columnName(Math.max(colCount - 1, 0));

    const cols = (sheet.colWidths && sheet.colWidths.length)
        ? `<cols>${sheet.colWidths
            .map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`)
            .join('')}</cols>`
        : '';

    const body = rows.map((row, rowIndex) => {
        const cells = row.map((value, colIndex) => {
            if (value === null || value === undefined || value === '') return '';
            const ref = `${columnName(colIndex)}${rowIndex + 1}`;
            if (typeof value === 'number' && isFinite(value)) {
                return `<c r="${ref}"><v>${value}</v></c>`;
            }
            // Inline string — avoids needing a sharedStrings.xml part.
            return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${xmlEscape(String(value))}</t></is></c>`;
        }).join('');
        return `<row r="${rowIndex + 1}">${cells}</row>`;
    }).join('');

    // Freeze the header row and add a filter over the used range, so a long
    // failure list is actually navigable.
    const freeze = rows.length > 1
        ? '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>'
        : '';
    const filter = rows.length > 1 && colCount > 0
        ? `<autoFilter ref="A1:${lastCol}${rows.length}"/>`
        : '';

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
        + `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">`
        + freeze
        + cols
        + `<sheetData>${body}</sheetData>`
        + filter
        + `</worksheet>`;
}

/**
 * Assemble a workbook. Returns a Blob ready to hand to `downloadBlob`.
 */
export function buildXlsx(sheets: XlsxSheet[]): Blob {
    const encoder = new TextEncoder();
    const list = (sheets && sheets.length) ? sheets : [{ name: 'Sheet1', rows: [] }];
    const names = list.map((s, i) => safeSheetName(s.name, i));

    const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
        + `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
        + `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>`
        + `<Default Extension="xml" ContentType="application/xml"/>`
        + `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>`
        + list.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')
        + `</Types>`;

    const rootRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
        + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
        + `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>`
        + `</Relationships>`;

    const workbook = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
        + `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"`
        + ` xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">`
        + `<sheets>`
        + names.map((name, i) => `<sheet name="${xmlEscape(name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')
        + `</sheets></workbook>`;

    const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
        + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
        + list.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('')
        + `</Relationships>`;

    const entries: ZipEntry[] = [
        { name: '[Content_Types].xml', data: encoder.encode(contentTypes) },
        { name: '_rels/.rels', data: encoder.encode(rootRels) },
        { name: 'xl/workbook.xml', data: encoder.encode(workbook) },
        { name: 'xl/_rels/workbook.xml.rels', data: encoder.encode(workbookRels) },
        ...list.map((sheet, i) => ({
            name: `xl/worksheets/sheet${i + 1}.xml`,
            data: encoder.encode(sheetXml(sheet))
        }))
    ];

    return zipStore(entries);
}
